import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LeasingCalculatorData } from 'modules/leasingCalculator/components/common/LeasingCalculatorData';
import { useTableParams } from '../../hooks/useTableParams';
import { Table } from '../table';
import {
  AppliedFiltersHolder,
  ButtonWrapper,
  FilterHolder,
  PageHeader,
  SearchUnavailableHelperText,
  SearchWrapper,
  StyledWrapper,
} from './styled';
import { Input } from '../input';
import { ContractGroup } from '../../contractsPage/components/helper';
import { LeasingOfferStatus } from '../../types/LeasingOffer';
import { BillDownloadUtil } from '../../billsPage/components/BillDownloadUtil';
import { deleteBillColumnFilters, Filter } from '../../billsPage/redux/slice';
import { BodyText } from '../typography';
import { Button, ButtonType } from '../button';
import {
  NavigationAndActionClose,
  NavigationAndActionInfoSmall,
} from '../icons';
import { useAppDispatch } from '../../hooks/appHooks';

export interface TableHookProps {
  type?: ContractGroup | LeasingOfferStatus;
  formData?: LeasingCalculatorData;
}

export interface TableWithSearchProps {
  type?: ContractGroup | LeasingOfferStatus;
  formData?: LeasingCalculatorData;
  showSearch?: boolean;
  showPaginationHelp?: boolean;
  useFormActions: (props?: TableHookProps) => any;
}

export const TableWithSearch: FC<TableWithSearchProps> = ({
  type,
  formData,
  showSearch,
  showPaginationHelp,
  useFormActions,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    totalPages,
    totalBills,
    onDownloadButtonClick,
    onCheckAllButtonClick,
    checkedBills,
    tableFilters,
    checkboxFilters,
    loading,
    tableColumns,
    tableRows,
    skeletonObject,
    fetchMoreData,
    emptyMessageText,
    columnsMaxWidth,
    setTableColumns,
    fetchMoreItemsWithFilters,
    fetchColumnCheckbox,
    showFilterForColumn,
    showSorting,
  } = useFormActions({ type, formData });

  const {
    tableParams,
    handleSearch,
    handleSearchChange,
    handlePageChange,
    handleSortChange,
    handleRowCountChange,
    handleFilterChange,
  } = useTableParams(
    fetchMoreData,
    fetchMoreItemsWithFilters,
    type,
    setTableColumns,
    tableFilters,
  );

  useEffect(() => {
    if (tableFilters && tableFilters.length > 0) {
      handleFilterChange();
    } else {
      handleSearch();
    }
  }, [tableFilters]);

  const deleteFilterButtonClick = (label: string) => {
    dispatch(deleteBillColumnFilters(label));
  };

  return (
    <StyledWrapper>
      {showSearch && (
        <PageHeader>
          <SearchWrapper>
            <Input
              type="search"
              defaultValue={tableParams.search}
              onChange={(event) => handleSearchChange(event.target.value)}
              inputChangeAction={(value) => handleSearch(value)}
              timeoutOnInputChangeInMs={2000}
              iconClickAction={() => handleSearch(tableParams.search)}
              placeholderText={t('search.searchPlaceholder')}
              onClear={() => handleSearchChange('')}
              disabled={loading || (tableFilters && tableFilters.length > 0)}
            />
          </SearchWrapper>
          {tableFilters && tableFilters.length > 0 && (
            <SearchUnavailableHelperText>
              <Button
                type={ButtonType.Action}
                icon={<NavigationAndActionInfoSmall />}
                title={t('billsPage.billUtil.unavailableGlobalSearchText')}
              />
            </SearchUnavailableHelperText>
          )}
        </PageHeader>
      )}
      {showPaginationHelp && (
        <div>
          {tableFilters.length > 0 && (
            <FilterHolder>
              <BodyText>
                {t('billsPage.billUtil.appliedFiltersHelperText')}
              </BodyText>
              <AppliedFiltersHolder>
                {tableFilters.map((filter: Filter) => (
                  <ButtonWrapper key={filter.column}>
                    <Button
                      type={ButtonType.Text}
                      icon={<NavigationAndActionClose />}
                      text={filter.column}
                      onClick={() => deleteFilterButtonClick(filter.column)}
                    />
                  </ButtonWrapper>
                ))}
              </AppliedFiltersHolder>
            </FilterHolder>
          )}
        </div>
      )}

      {showPaginationHelp && (
        <BillDownloadUtil
          checkedBills={checkedBills}
          tableRows={tableRows}
          onCheckAllButtonClick={onCheckAllButtonClick}
          onDownloadButtonClick={onDownloadButtonClick}
        />
      )}
      <Table
        columns={tableColumns}
        rows={loading ? skeletonObject : tableRows}
        pageable
        showPaginationHelp={showPaginationHelp}
        currentPage={tableParams.currentPage}
        onPageChange={handlePageChange}
        onRowCountChange={handleRowCountChange}
        maxPages={totalPages}
        totalBills={totalBills}
        tableFilters={tableFilters}
        checkboxFilters={checkboxFilters}
        onSortChange={handleSortChange}
        emptyMessageText={emptyMessageText}
        columnsMaxWidth={columnsMaxWidth}
        fetchColumnCheckbox={fetchColumnCheckbox}
        showFilterForColumn={showFilterForColumn}
        showSorting={showSorting}
      />
    </StyledWrapper>
  );
};
